import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { TourProvider } from '@reactour/tour';
import { useTour } from '@reactour/tour';
import { useGlobalTour } from '../contexts/TourContext';
import { useTranslatedSteps } from './stepsTour';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  Paper,
  IconButton,
  AppBar,
  Toolbar,
  CircularProgress,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useSnackbar } from 'notistack';
import { conversations as conversationsApi, membership, onboarding, type ChannelInfo } from '../services/api';
import { useTranslation } from '../contexts/TranslationContext';
import dayjs from 'dayjs';
import jalaliday from 'jalaliday';
import 'dayjs/locale/fa';
import VideoProvider from '../components/VideoProvider';
import { videoUrls, videoTitles } from '../utils/videoUrls';
import { toPersianDigits } from '../utils/validation';
import { parseMarkdown, handleMarkdownButtonClick } from '../utils/parseMarkdown';
dayjs.extend(jalaliday);

const extractTimeFromDatetime = (dateString: string): string => {
  const match = dateString.match(/T(\d{2}:\d{2})/);
  return match ? match[1] : dayjs(dateString).format('HH:mm');
};

const getCalendarDayDiff = (dateString: string): number  => {
  const messageDate = new Date(dateString);
  const now = new Date();
  const messageDayStart = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((todayStart.getTime() - messageDayStart.getTime()) / 86400000);
};

const formatLastMessageDatetime = (dateString: string): string => {
  const time = extractTimeFromDatetime(dateString);
  const dayDiff = getCalendarDayDiff(dateString);

  let result: string;
  if (dayDiff === 0) {
    result = `${time} امروز`;
  } else if (dayDiff === 1) {
    result = `${time} دیروز`;
  } else {
    const shamsiDate = dayjs(dateString).calendar('jalali').locale('fa');
    result = ` ${time} ${shamsiDate.format('D MMMM')}`;
  }

  return toPersianDigits(result);
};

interface ChannelUser {
  username: string;
  thread_id: string;
  last_message_datetime?: string;
}

interface Message {
  id: number;
  thread_id: string;
  username: string;
  channel_name: string;
  input_message: string;
  output_message: string;
  input_tokens: number;
  output_tokens: number;
  model_name: string;
  created_at: string;
}

interface ChannelListResponse {
  total: number;
  channels: string[];
}

interface ChannelUserResponse {
  user: string;
  last_message_datetime: string;
}

interface UsernameListResponse {
  total: number;
  users: ChannelUserResponse[];
}

interface MessageListResponse {
  total: number;
  messages: Message[];
}

type ViewLayer = 'channels' | 'users' | 'messages';

// Default channels that should always be displayed
const DEFAULT_CHANNELS = ['website_chatbox','telegram','instagram', 'bale', 'knowledgeQuery'];

const CONNECT_CHANNELS = [
  { channel: 'instagram', labelKey: 'conversations.emptyState.connectInstagram', enabledKey: 'instagram_enabled' as const },
  { channel: 'telegram', labelKey: 'conversations.emptyState.connectTelegram', enabledKey: 'telegram_enabled' as const },
  { channel: 'website_chatbox', labelKey: 'conversations.emptyState.connectWebsite', enabledKey: 'website_chatbox_enabled' as const },
  { channel: 'bale', labelKey: 'conversations.emptyState.connectBale', enabledKey: 'bale_enabled' as const },
] as const;

const AIDA_SETTINGS_CHANNELS_TAB = 5;

const MESSAGE_BACKGROUND_SX = {
  bgcolor: '#F5F6FA',
  backgroundImage: 'url(/images/Aida-Chatbox-Backgorund.png)',
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  backgroundRepeat: 'repeat',
} as const;

const MESSAGE_PAGINATION_BUTTON_SX = {
  width: 'fit-content',
  minWidth: 'unset',
  mx: 'auto',
  px: { xs: 1.5, sm: 2 },
  py: 0.5,
  borderRadius: '20px',
  fontSize: { xs: '0.72rem', sm: '0.8rem' },
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  borderColor: '#1C1C1C',
  color: '#1C1C1C',
  '&:hover': {
    backgroundColor: '#e0e0e0',
    color: '#1C1C1C',
  },
} as const;

const MESSAGE_PAGINATION_BUTTON_WRAPPER_SX = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  alignSelf: 'center',
  width: 'fit-content',
  maxWidth: '100%',
  mx: 'auto',
  mb: 1.5,
  flexShrink: 0,
} as const;

const USER_LIST_CARD_CONTENT_SX = {
  height: '100%',
  overflow: 'hidden',
  p: 0,
  '&:last-child': { pb: 0 },
} as const;

const ConversationsPageContent: FC = () => {
  const navigate = useNavigate();
  const { setIsOpen } = useTour();
  const { setTourFunction } = useGlobalTour();
  const [currentLayer, setCurrentLayer] = useState<ViewLayer>('channels');
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [selectedUsername, setSelectedUsername] = useState<string>('');
  
  const [channels, setChannels] = useState<string[]>(DEFAULT_CHANNELS);
  const [users, setUsers] = useState<ChannelUser[]>([]);
  const [displayedUsers, setDisplayedUsers] = useState<ChannelUser[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [messagesTotal, setMessagesTotal] = useState(0);
  
  // Messages pagination states
  const [messagesSkip, setMessagesSkip] = useState(0);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  
  // Lazy loading states
  const [hasMoreUsers, setHasMoreUsers] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
  
  // Virtual scrolling states
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [itemHeight] = useState(80); // Fixed height for each user item
  const [bufferSize] = useState(10); // Extra items to render for smooth scrolling
  const [isVirtualScrollingEnabled, setIsVirtualScrollingEnabled] = useState(false);
  
  // Cache states to prevent unnecessary reloads
  const [channelsCache, setChannelsCache] = useState<string[] | null>(null);
  const [usersCache, setUsersCache] = useState<Map<string, ChannelUser[]>>(new Map());
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [guideModalOpen, setGuideModalOpen] = useState(false);
  const [channelConnectStatus, setChannelConnectStatus] = useState<ChannelInfo | null>(null);
  const [channelConnectStatusLoading, setChannelConnectStatusLoading] = useState(true);
  const [isMemberUser, setIsMemberUser] = useState(false);
  const [membershipUiModules, setMembershipUiModules] = useState<string[] | null>(null);
  const [membershipLoading, setMembershipLoading] = useState(true);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesTopRef = useRef<HTMLDivElement>(null);
  const usersListRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pendingMessageScrollRef = useRef<'bottom' | 'top' | null>(null);
  const channelDockScrollRef = useRef<HTMLDivElement>(null);
  const [channelDockScrollable, setChannelDockScrollable] = useState(false);

  const applyMessageScroll = (position: 'bottom' | 'top') => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (position === 'bottom') {
      // column-reverse: scrollTop 0 = newest messages at visual bottom
      container.scrollTop = 0;
      container.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }

    const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
    container.scrollTop = maxScroll;
    container.scrollTo({ top: maxScroll, behavior: 'auto' });
  };

  const applyMessageScrollWithRetries = (position: 'bottom' | 'top') => {
    applyMessageScroll(position);
    requestAnimationFrame(() => {
      applyMessageScroll(position);
      requestAnimationFrame(() => applyMessageScroll(position));
    });
    [0, 50, 150, 300, 600, 1000, 1500].forEach((delay) => {
      setTimeout(() => applyMessageScroll(position), delay);
    });
  };

  const { enqueueSnackbar } = useSnackbar();
  const { t, tArray } = useTranslation();
  let flag = false;

  
  // !Combine default channels with API channels
  const combineChannels = (apiChannels: string[]): string[] => {
    // Start with default channels
    const combinedChannels = [...DEFAULT_CHANNELS];
    
    // Create a set of default channel names (case-insensitive) for quick lookup
    const defaultChannelsLower = DEFAULT_CHANNELS.map(ch => ch.toLowerCase());
    
    // Add API channels that are not in default channels (case-insensitive comparison)
    apiChannels.forEach(channel => {
      if (!defaultChannelsLower.includes(channel.toLowerCase())) {
        combinedChannels.push(channel);
      }
    });
    
    return combinedChannels;
  };

  // !Fetch channels with caching
  const fetchChannels = async (forceRefresh = false) => {
    // Always show default channels immediately
    setChannels(DEFAULT_CHANNELS);
    setLoading(false);

    // Check cache first for API channels
    if (channelsCache && !forceRefresh) {
      const combined = combineChannels(channelsCache);
      setChannels(combined);
      return;
    }

    try {
      setChannelsLoading(true);
      const response = await conversationsApi.getChannels();
      const data: ChannelListResponse = response.data;
      
      // Combine default channels with API channels
      const combinedChannels = combineChannels(data.channels);
      setChannels(combinedChannels);
      setChannelsCache(data.channels); // Cache only API channels
    } catch (error: any) {
      if (error.response?.status === 401) {
        if (!flag) {
          enqueueSnackbar(t('conversations.error.sessionExpired'), { 
            variant: 'error',
            autoHideDuration: 5000,
            style: {
              padding: '20px',
              marginTop: '20px', // Add margin top
            },
          });
          flag = true;
          }
          setTimeout(() => {
            localStorage.removeItem('token');
            window.location.href = '/auth/auth';
          }, 4000);
      } else {
        // Even if API fails, keep default channels visible
        enqueueSnackbar(t('conversations.error.fetchChannelsError'), { variant: 'error' });
      }
    } finally {
      setChannelsLoading(false);
      setLoading(false);
    }
  };

  // !Fetch users for selected channel with lazy loading
  const fetchChannelUsers = async (channelName: string) => {
    try {
      setUsersLoading(true);
      setLoading(true);
      const response = await conversationsApi.getChannelUsers(channelName);
      const data: UsernameListResponse = response.data;
      
      const userObjects: ChannelUser[] = (data.users ?? [])
        .map(({ user, last_message_datetime }) => ({
          username: user,
          thread_id: user,
          last_message_datetime,
        }))
        .sort((a, b) => {
          const timeA = a.last_message_datetime ? new Date(a.last_message_datetime).getTime() : 0;
          const timeB = b.last_message_datetime ? new Date(b.last_message_datetime).getTime() : 0;
          return timeB - timeA;
        });
      
      setUsers(userObjects);
      // Cache the result
      setUsersCache(prev => new Map(prev).set(channelName, userObjects));
      
      // Initialize lazy loading
      initializeLazyLoading(userObjects);
    } catch (error: any) {
      if (error.response?.status === 401) {
        if (!flag) {
          enqueueSnackbar(t('conversations.error.sessionExpired'), { 
            variant: 'error',
            autoHideDuration: 5000,
            style: {
              padding: '20px',
              marginTop: '20px', // Add margin top
            },
          });
          flag = true;
        }
        setTimeout(() => {
          localStorage.removeItem('token');
          window.location.href = '/auth/auth';
        }, 4000);
      } else {
        enqueueSnackbar(t('conversations.error.fetchUsersError'), { variant: 'error' });
      }
    } finally {
      setUsersLoading(false);
      setLoading(false);
    }
  };

  // Initialize lazy loading for users
  const initializeLazyLoading = (allUsers: ChannelUser[]) => {
    const USERS_PER_PAGE = 20;
    const firstPageUsers = allUsers.slice(0, USERS_PER_PAGE);
    setDisplayedUsers(firstPageUsers);
    setCurrentPage(0);
    setHasMoreUsers(allUsers.length > USERS_PER_PAGE);
  };

  //! Flush all user states completely
  const flushUserStates = () => {
    setUsers([]);
    setDisplayedUsers([]);
    setHasMoreUsers(false);
    setCurrentPage(0);
    setLoadingMoreUsers(false);
    setSelectedUsername('');
  };

  // !Load more users for lazy loading
  const loadMoreUsers = () => {
    if (loadingMoreUsers || !hasMoreUsers) return;
    
    setLoadingMoreUsers(true);
    const USERS_PER_PAGE = 20;
    const nextPage = currentPage + 1;
    const startIndex = nextPage * USERS_PER_PAGE;
    const endIndex = startIndex + USERS_PER_PAGE;
    
    const nextBatchUsers = users.slice(startIndex, endIndex);
    setDisplayedUsers(prev => [...prev, ...nextBatchUsers]);
    setCurrentPage(nextPage);
    setHasMoreUsers(endIndex < users.length);
    setLoadingMoreUsers(false);
  };

  // !Virtual scrolling calculations with better error handling
  const getVisibleRange = () => {
    if (!containerHeight || users.length === 0) {
      return { startIndex: 0, endIndex: Math.min(20, displayedUsers.length) };
    }
    
    // Calculate range based on scroll position and all users (for correct scrollbar)
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - bufferSize);
    const visibleItems = Math.ceil(containerHeight / itemHeight);
    const endIndex = startIndex + visibleItems + (bufferSize * 2);
    
    // Return the calculated range (getVisibleUsers will clamp it to displayedUsers)
    return { startIndex, endIndex };
  };

  // !Handle scroll event for virtual scrolling with debouncing and auto-loading
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const newScrollTop = target.scrollTop;
    
    // Only update if scroll position changed significantly
    if (Math.abs(newScrollTop - scrollTop) > 5) {
      setScrollTop(newScrollTop);
    }
    
    // Auto-load more users when scrolling near the end of displayedUsers
    if (hasMoreUsers && !loadingMoreUsers) {
      const currentUserIndex = Math.floor(newScrollTop / itemHeight);
      const displayedCount = displayedUsers.length;
      
      // Load more when we're within 10 users of the end of displayedUsers
      if (currentUserIndex >= displayedCount - 10) {
        loadMoreUsers();
      }
    }
  };

  // !Get visible users for virtual scrolling with fallback
  const getVisibleUsers = () => {
    if (!isVirtualScrollingEnabled || displayedUsers.length <= 50) {
      return displayedUsers;
    }
    
    const { startIndex, endIndex } = getVisibleRange();
    // Only show users that are actually loaded in displayedUsers
    // startIndex and endIndex are based on all users, but we only render displayedUsers
    const clampedStartIndex = Math.max(0, Math.min(startIndex, displayedUsers.length));
    const clampedEndIndex = Math.min(endIndex, displayedUsers.length);
    
    return displayedUsers.slice(clampedStartIndex, clampedEndIndex);
  };

  // !Get total height for virtual scrolling
  const getTotalHeight = () => {
    if (!isVirtualScrollingEnabled) {
      return 'auto';
    }
    // Use all users length to show full scrollable area, even if not all loaded
    return users.length * itemHeight;
  };

  // !Get offset for virtual scrolling
  const getOffsetY = () => {
    if (!isVirtualScrollingEnabled) {
      return 0;
    }
    const { startIndex } = getVisibleRange();
    // Use clamped startIndex to match what we're actually rendering
    const clampedStartIndex = Math.max(0, Math.min(startIndex, displayedUsers.length));
    return clampedStartIndex * itemHeight;
  };

  // !Fetch messages for selected thread
  const fetchThreadMessages = async (channelName: string, username: string, skip: number = 0, showLoading: boolean = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      const response = await conversationsApi.getThreadMessages(channelName, username, {
        skip: skip,
        limit: 50 // Always show 50 messages
      });
      const data: MessageListResponse = response.data;
      
      // Always replace messages (no append) - show only 50 messages
      setMessages(data.messages);
      setMessagesTotal(data.total);
      setMessagesSkip(skip);
      
      // Check if there are more messages to load (after current skip + 50)
      setHasMoreMessages((skip + 50) < data.total);
    } catch (error) {
      enqueueSnackbar(t('conversations.error.fetchMessagesError'), { variant: 'error' });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
      setLoadingMoreMessages(false);
    }
  };

  // !دکمه بالایی — اسکرول به پایین‌ترین حد
  const loadMoreMessages = async () => {
    if (!selectedChannel || !selectedUsername || loadingMoreMessages || !hasMoreMessages) return;
    
    setLoadingMoreMessages(true);
    pendingMessageScrollRef.current = 'bottom';
    const nextSkip = messagesSkip + 50;
    await fetchThreadMessages(selectedChannel, selectedUsername, nextSkip, false);
  };

  // !دکمه پایینی — اسکرول به بالاترین حد
  const loadPreviousMessages = async () => {
    if (!selectedChannel || !selectedUsername || loadingMoreMessages || messagesSkip <= 0) return;
    
    setLoadingMoreMessages(true);
    pendingMessageScrollRef.current = 'top';
    const previousSkip = Math.max(0, messagesSkip - 50);
    await fetchThreadMessages(selectedChannel, selectedUsername, previousSkip, false);
  };

  const getOrderedMessages = (msgs: Message[]) =>
    [...msgs].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

  // !Handle channel selection
  const handleChannelSelect = (channelName: string) => {
    setSelectedChannel(channelName);
    setCurrentLayer('users');
    
    // Reset scroll position when selecting new channel
    setScrollTop(0);
    
    // Flush all user states completely
    flushUserStates();
    
    // Clear all cache to prevent mixing users from different channels
    setUsersCache(new Map());
    
    // Force a small delay to ensure state flush completes
    setTimeout(() => {
      // Always fetch fresh data to ensure clean state
      fetchChannelUsers(channelName);
    }, 10);
  };

  // !Handle user selection
  const handleUserSelect = (user: ChannelUser) => {
    setSelectedUsername(user.username);
    setMessages([]);
    setMessagesSkip(0);
    setHasMoreMessages(false);
    setCurrentLayer('messages');
    pendingMessageScrollRef.current = 'bottom';
    fetchThreadMessages(selectedChannel, user.username, 0);
  };

  // !Handle back navigation
  const handleBack = () => {
    if (currentLayer === 'messages') {
      setCurrentLayer('users');
      setSelectedUsername('');
      // Reset scroll position when going back to users
      setScrollTop(0);
      // Restore cached users data when going back
      if (usersCache.has(selectedChannel)) {
        const cachedUsers = usersCache.get(selectedChannel)!;
        setUsers(cachedUsers);
        initializeLazyLoading(cachedUsers);
        setLoading(false);
      }
    } else if (currentLayer === 'users') {
      setCurrentLayer('channels');
      setSelectedChannel('');
      // Flush all user states when going back to channels
      flushUserStates();
      // Reset scroll position when going back to channels
      setScrollTop(0);
      // Restore cached channels data when going back, combining with default channels
      if (channelsCache) {
        const combined = combineChannels(channelsCache);
        setChannels(combined);
        setLoading(false);
      } else {
        // If no cache, show default channels
        setChannels(DEFAULT_CHANNELS);
        setLoading(false);
      }
    }
  };

  // !Apply scroll after messages render in DOM
  useLayoutEffect(() => {
    if (currentLayer !== 'messages' || loading || messages.length === 0) {
      return;
    }

    const pendingScroll = pendingMessageScrollRef.current;
    if (pendingScroll) {
      pendingMessageScrollRef.current = null;
      applyMessageScrollWithRetries(pendingScroll);
      return;
    }

    if (messagesSkip === 0) {
      applyMessageScrollWithRetries('bottom');
    }
  }, [messages, messagesSkip, loading, currentLayer, selectedUsername]);

  useLayoutEffect(() => {
    const scrollEl = channelDockScrollRef.current;
    if (!scrollEl) return;

    const updateScrollable = () => {
      setChannelDockScrollable(scrollEl.scrollWidth > scrollEl.clientWidth + 1);
    };

    updateScrollable();
    const observer = new ResizeObserver(updateScrollable);
    observer.observe(scrollEl);

    return () => observer.disconnect();
  }, [channels, channelsLoading, selectedChannel]);

  // !Keep newest messages visible while conversation content layout settles
  useEffect(() => {
    if (currentLayer !== 'messages' || loading || messages.length === 0 || messagesSkip !== 0) {
      return;
    }

    const container = messagesContainerRef.current;
    if (!container) return;

    const scrollToBottom = () => {
      container.scrollTop = 0;
    };

    let rafId = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(scrollToBottom);
    });

    observer.observe(container);
    scrollToBottom();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [messages, loading, currentLayer, selectedUsername, messagesSkip]);

  // !Register tour function with global tour context
  useEffect(() => {
    setTourFunction(() => setIsOpen(true));
  }, [setTourFunction, setIsOpen]);

  // !Clear cache function
  const clearCache = () => {
    setChannelsCache(null);
    setUsersCache(new Map());
    setDisplayedUsers([]);
    setHasMoreUsers(false);
    setCurrentPage(0);
    setLoadingMoreUsers(false);
  };

  // !Set container height and enable virtual scrolling when needed
  useEffect(() => {
    const updateContainerHeight = () => {
      if (usersListRef.current) {
        const height = usersListRef.current.clientHeight;
        setContainerHeight(height);
        
        // Enable virtual scrolling only if we have many users and container is ready
        setIsVirtualScrollingEnabled(
          users.length > 100 && 
          height > 0 && 
          currentLayer === 'users'
        );
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(updateContainerHeight, 100);
    window.addEventListener('resize', updateContainerHeight);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateContainerHeight);
    };
  }, [currentLayer, users.length]);

  // !Auto-load more users when scrolling near the end
  useEffect(() => {
    if (!isVirtualScrollingEnabled || !hasMoreUsers || loadingMoreUsers || !containerHeight || users.length === 0) {
      return;
    }

    // Calculate current visible range
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - bufferSize);
    const visibleItems = Math.ceil(containerHeight / itemHeight);
    const endIndex = startIndex + visibleItems + (bufferSize * 2);
    
    // If we're trying to render users beyond what's loaded, load more
    if (endIndex >= displayedUsers.length - 5 && displayedUsers.length < users.length) {
      loadMoreUsers();
    }
  }, [scrollTop, containerHeight, displayedUsers.length, users.length, isVirtualScrollingEnabled, hasMoreUsers, loadingMoreUsers, itemHeight, bufferSize]);

  // !Initial load
  useEffect(() => {
    const initializePage = async () => {
      let memberUser = false;
      let uiModules: string[] | null = null;

      try {
        const response = await membership.getMe();
        memberUser = response.data.is_member;
        uiModules = response.data.ui_modules ?? [];
        setIsMemberUser(memberUser);
        setMembershipUiModules(uiModules);
      } catch (error) {
        console.error('Failed to fetch membership info:', error);
        setIsMemberUser(false);
        setMembershipUiModules(null);
      } finally {
        setMembershipLoading(false);
      }

      const hasChannelsAccess =
        !memberUser || (uiModules?.includes('channels') ?? true);

      if (!hasChannelsAccess) {
        setChannels(DEFAULT_CHANNELS);
        setLoading(false);
        setChannelConnectStatusLoading(false);
        return;
      }

      await fetchChannels();

      try {
        const response = await onboarding.getChannelInfo();
        setChannelConnectStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch channel connect status:', error);
        setChannelConnectStatus(null);
      } finally {
        setChannelConnectStatusLoading(false);
      }
    };

    void initializePage();
    
    // !Clear cache when component unmounts (user leaves the page)
    return () => {
      clearCache();
    };
  }, []);

  // !Helper function to format date and time
  

  // !Helper functions for channel styling
  const getChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'instagram':
        return <img src="/images/instagram-2016-logo-svgrepo-com.svg" alt="Instagram" style={{ width: '24px', height: '24px' }} />;
      case 'telegram':
        return <img src="/images/telegram-logo-svgrepo-com.svg" alt="Telegram" style={{ width: '24px', height: '24px' }} />;
      case 'website_chatbox':
        return <img src="/images/website-click-svgrepo-com.svg" alt="Website Chatbox" style={{ width: '24px', height: '24px' }} />;
      case 'bale':
        return <img src="/images/bale-svgrepo-com.svg" alt="Bale" style={{ width: '24px', height: '24px' }} />;
      case 'knowledgequery':
        return <MenuBookIcon sx={{ width: 24, height: 24, fontSize: 24, color: '#455A64' }} />;
      case 'unknown':
        return <img src="/images/website-click-svgrepo-com.svg" alt="Unknown" style={{ width: '24px', height: '24px' }} />;
      default:
        return <img src="/images/website-click-svgrepo-com.svg" alt="Default" style={{ width: '24px', height: '24px' }} />;
    }
  };

  // !Get channel color
  const getChannelColor = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'instagram':
        return '#F8BBD0'; // Instagram pink
      case 'telegram':
        return '#BBDEFB'; // Telegram blue
      case 'website_chatbox':
        return '#FFCCBC'; // Orange for Bale
        case 'bale':
        return '#DCEDC8'; // Green for chat
      case 'knowledgequery':
        return '#CFD8DC'; // Brown for knowledge query
      case 'unknown':
        return '#9E9E9E'; // Gray for unknown
      default:
        return '#3B82F6'; // Default blue
    }
  };

  // !Get channel color for avatar
  const getChannelColorAvatar = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'instagram':
        return '#F9D4E1'; // Instagram pink
      case 'telegram':
        return '#D0ECFA'; // Telegram blue
      case 'website_chatbox':
        return '#FFE0CC'; // Orange for Bale
        case 'bale':
        return '#D6F5DC'; // Green for chat
      case 'unknown':
        return '#E6E6E6'; // Gray for unknown
      default:
        return '#D8E6FF'; // Default blue
    }
  };

  // !Render channel list (Layer 1)
  const renderChannelList = () => {
    return (
      <Card sx={{borderRadius: 0 , boxShadow: 0 , display: 'flex' , alignItems: 'center' , justifyContent: 'center'}}>
        <CardContent className="first-step" sx={{padding: 0}}>
          {channelsLoading ? (
            <Box display="flex" justifyContent="center" py={0}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {channels.map((channel) => (
                <ListItem key={channel} disablePadding>
                  <ListItemButton onClick={() => handleChannelSelect(channel)}>
                    <Avatar sx={{bgcolor: getChannelColor(channel)}}>
                      {getChannelIcon(channel)}
                    </Avatar>
                  </ListItemButton>
                </ListItem>
              ))}
              {channels.length === 0 && (
                <Typography color="text.secondary" textAlign="center" py={4}>
                  {t('conversations.noChannels')}
                </Typography>
              )}
            </List>
          )}
        </CardContent>
      </Card>
    );
  };

  // !Render user list (Layer 2) with improved virtual scrolling
  const renderUserList = () => {
    const visibleUsers = getVisibleUsers();
    const totalHeight = getTotalHeight();
    const offsetY = getOffsetY();
    const { startIndex } = getVisibleRange();

    // !Fallback to regular rendering for small lists
    if (!isVirtualScrollingEnabled) {
      return (
        <Card sx={{ borderRadius: 0, boxShadow: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={USER_LIST_CARD_CONTENT_SX}>
            {usersLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ height: '100%', overflowY: 'auto', padding: 0}}>
                {displayedUsers.map((user) => (
                  <ListItem key={user.thread_id} disablePadding>
                    <ListItemButton 
                      onClick={() => handleUserSelect(user)} 
                      sx={{ 
                        maxWidth: '100%', 
                        paddingLeft: {xs: 0},
                        backgroundColor: selectedUsername === user.username ? 'action.selected' : 'transparent',
                        '&:hover': {
                          backgroundColor: selectedUsername === user.username ? 'action.selected' : 'action.hover'
                        }
                      }}
                    >
                      <Box sx={{ position: 'relative', mr: 2 , padding: '0px'}}>
                        <Avatar sx={{ bgcolor: getChannelColorAvatar(selectedChannel) }}>
                          <PersonIcon />
                        </Avatar>
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: -8,
                            right: -8,
                            bgcolor: getChannelColor(selectedChannel),
                            borderRadius: '50%',
                            p: 0.1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 1,
                          }}
                        >
                          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', scale: '0.8', color: 'white'}}>
                            {getChannelIcon(selectedChannel)}
                          </Box>
                        </Box>
                      </Box>
                      <ListItemText sx={{ maxWidth: '100%'}}
                        primary={user.username}
                        secondary={
                          user.last_message_datetime
                            ? formatLastMessageDatetime(user.last_message_datetime)
                            : undefined
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
                {displayedUsers.length === 0 && (
                  <Typography color="text.secondary" textAlign="center" py={4}>
                    {t('conversations.noUsers')}
                  </Typography>
                )}
                {/* Load More Button */}
                {hasMoreUsers && displayedUsers.length > 0 && (
                  <ListItem disablePadding>
                    <Box sx={{ width: '100%', p: 2, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={loadMoreUsers}
                        disabled={loadingMoreUsers}
                        sx={{
                          width: '100%',
                          py: 1,
                          borderRadius: 2,
                          borderColor: getChannelColor(selectedChannel),
                          color: getChannelColor(selectedChannel),
                          '&:hover': {
                            backgroundColor: getChannelColor(selectedChannel),
                            color: 'white',
                          },
                        }}
                      >
                        {loadingMoreUsers ? (
                          <CircularProgress size={20} />
                        ) : (
                          t('conversations.moreUsers')
                        )}
                      </Button>
                    </Box>
                  </ListItem>
                )}
              </List>
            )}
          </CardContent>
        </Card>
      );
    }

    // !Virtual scrolling rendering
    return (
      <Card sx={{ borderRadius: 0, boxShadow: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={USER_LIST_CARD_CONTENT_SX}>
          {usersLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              ref={usersListRef}
              sx={{ 
                height: '100%', 
                overflowY: 'auto', 
                position: 'relative'
              }}
              onScroll={handleScroll}
            >
              {/* Virtual scrolling container */}
              <Box sx={{ height: totalHeight, position: 'relative' }}>
                <Box sx={{ 
                  position: 'absolute', 
                  top: offsetY, 
                  width: '100%'
                }}>
                  {visibleUsers.map((user) => (
                    <Box
                      key={user.thread_id}
                      sx={{
                        height: itemHeight,
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    >
                      <ListItem disablePadding sx={{ width: '100%' }}>
                        <ListItemButton 
                          onClick={() => handleUserSelect(user)} 
                          sx={{ 
                            maxWidth: '100%', 
                            paddingLeft: {xs: 0},
                            height: itemHeight,
                            minHeight: itemHeight,
                            backgroundColor: selectedUsername === user.username ? 'action.selected' : 'transparent',
                            '&:hover': {
                              backgroundColor: selectedUsername === user.username ? 'action.selected' : 'action.hover'
                            }
                          }}
                        >
                          <Box sx={{ position: 'relative', mr: 2 , padding: '0px'}}>
                            <Avatar sx={{ bgcolor: getChannelColorAvatar(selectedChannel) }}>
                              <PersonIcon />
                            </Avatar>
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: -8,
                                right: -8,
                                bgcolor: getChannelColor(selectedChannel),
                                borderRadius: '50%',
                                p: 0.1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: 1,
                              }}
                            >
                              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', scale: '0.8', color: 'white'}}>
                                {getChannelIcon(selectedChannel)}
                              </Box>
                            </Box>
                          </Box>
                          <ListItemText sx={{ maxWidth: '100%'}}
                            primary={user.username}
                            secondary={
                              user.last_message_datetime
                                ? formatLastMessageDatetime(user.last_message_datetime)
                                : undefined
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    </Box>
                  ))}
                  
                  {/* Load More Button - only show when near the end */}
                  {hasMoreUsers && displayedUsers.length > 0 && startIndex + visibleUsers.length >= displayedUsers.length - 5 && (
                    <Box sx={{ 
                      height: 60, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      p: 2 
                    }}>
                      <Button
                        variant="outlined"
                        onClick={loadMoreUsers}
                        disabled={loadingMoreUsers}
                        sx={{
                          width: '100%',
                          py: 1,
                          borderRadius: 2,
                          borderColor: getChannelColor(selectedChannel),
                          color: getChannelColor(selectedChannel),
                          '&:hover': {
                            backgroundColor: getChannelColor(selectedChannel),
                            color: 'white',
                          },
                        }}
                      >
                        {loadingMoreUsers ? (
                          <CircularProgress size={20} />
                        ) : (
                          t('conversations.moreUsers')
                        )}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
              
              {displayedUsers.length === 0 && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)' 
                }}>
                  <Typography color="text.secondary" textAlign="center">
                    {t('conversations.noUsers')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // !Get channel label
  const getChannelLabel = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'instagram':
        return t('dashboard.activeChannels.instagram');
      case 'telegram':
        return t('dashboard.activeChannels.telegram');
      case 'website_chatbox':
        return t('dashboard.activeChannels.website');
      case 'bale':
        return t('dashboard.activeChannels.bale');
      default:
        return channel;
    }
  };

  const renderGuideContent = () => {
    const purposeItems = tArray('conversations.guide.purposeItems');

    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
          {t('conversations.guide.intro')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.8 }}>
          {t('conversations.guide.structureIntro')}
        </Typography>

        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: '#1C1C1C' }}>
          {t('conversations.guide.channelsTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.8 }}>
          {t('conversations.guide.channelsDescription')}
        </Typography>
        <Box sx={{ mb: 2.5, pl: 1 }}>
          {DEFAULT_CHANNELS.map((channel) => (
            <Box key={channel} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Avatar sx={{ width: 28, height: 28, bgcolor: getChannelColor(channel) }}>
                {getChannelIcon(channel)}
              </Avatar>
              <Typography variant="body2" color="text.secondary">
                {getChannelLabel(channel)}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.8 }}>
          {t('conversations.guide.channelsHint')}
        </Typography>

        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: '#1C1C1C' }}>
          {t('conversations.guide.usersTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
          {t('conversations.guide.usersDescription')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.8 }}>
          {t('conversations.guide.usersHint')}
        </Typography>

        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: '#1C1C1C' }}>
          {t('conversations.guide.messagesTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.8 }}>
          {t('conversations.guide.messagesDescription')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.8 }}>
          {t('conversations.guide.messagesHint')}
        </Typography>

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.8, fontWeight: 600 }}>
          {t('conversations.guide.purposeIntro')}
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2.5 }}>
          {purposeItems.map((item) => (
            <Typography
              key={item}
              component="li"
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.75, lineHeight: 1.8 }}
            >
              {item}
            </Typography>
          ))}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
          {t('conversations.guide.closing')}
        </Typography>
      </>
    );
  };

  const handleNavigateToChannels = () => {
    navigate(`/aida-settings?tab=${AIDA_SETTINGS_CHANNELS_TAB}`);
  };

  const hasChannelsModule =
    !isMemberUser || (membershipUiModules?.includes('channels') ?? true);

  const getUnconnectedChannels = () => {
    if (!channelConnectStatus) {
      return CONNECT_CHANNELS;
    }

    return CONNECT_CHANNELS.filter(
      ({ enabledKey }) => !channelConnectStatus[enabledKey],
    );
  };

  const renderInlineGuide = () => (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
        py: 3,
        bgcolor: '#fafbfc',
      }}
    >
      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#1C1C1C' }}>
        {t('conversations.guide.title')}
      </Typography>
      {renderGuideContent()}
    </Box>
  );

  const renderMessagesGuide = () => {
    if (channelConnectStatusLoading || membershipLoading) {
      return (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafbfc' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!hasChannelsModule) {
      return renderInlineGuide();
    }

    const unconnectedChannels = getUnconnectedChannels();

    if (unconnectedChannels.length === 0) {
      return renderInlineGuide();
    }

    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 3, md: 4 },
          py: 4,
          bgcolor: '#fafbfc',
          overflowY: 'auto',
        }}
      >
        <Typography
          variant="body1"
          textAlign="center"
          sx={{
            color: '#4a4a4a',
            fontWeight: 500,
            maxWidth: 480,
            mb: 4,
            lineHeight: 1.7,
          }}
        >
          {t('conversations.emptyState.title')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            width: '100%',
            maxWidth: 520,
            mb: 4,
          }}
        >
          {unconnectedChannels.map(({ channel, labelKey }) => (
            <Box
              key={channel}
              component="button"
              type="button"
              onClick={handleNavigateToChannels}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.75,
                minWidth: 220,
                bgcolor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                cursor: 'pointer',
                textAlign: 'right',
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  borderColor: '#bdbdbd',
                },
              }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: getChannelColor(channel), flexShrink: 0 }}>
                {getChannelIcon(channel)}
              </Avatar>
              <Typography variant="body2" fontWeight={500} sx={{ color: '#1C1C1C' }}>
                {t(labelKey)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Link
          component="button"
          type="button"
          onClick={() => setGuideModalOpen(true)}
          underline="hover"
          sx={{
            color: '#1976d2',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            fontFamily: 'inherit',
          }}
        >
          {t('conversations.emptyState.guideLink')}
        </Link>

        <Dialog
          open={guideModalOpen}
          onClose={() => setGuideModalOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: '12px' } }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: 700,
              pr: 1,
            }}
          >
            {t('conversations.guide.title')}
            <IconButton aria-label="close" onClick={() => setGuideModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
            {renderGuideContent()}
          </DialogContent>
        </Dialog>
      </Box>
    );
  };

  // !Render conversation messages (Layer 3)
  const renderMessageList = () => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' , borderRadius: 0 , boxShadow: 0}}>
      <AppBar position="static" elevation={0} sx={{ bgcolor: getChannelColor(selectedChannel) }}>
        <Toolbar variant="dense" sx={{ flexDirection: 'row', alignItems: 'center', gap: 1, px: 1, minHeight: 48, py: { md: '16px', lg: '16px'} }}>
          <IconButton edge="start" color="inherit" size="small" onClick={handleBack} sx={{ mr: 1 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar sx={{ width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, bgcolor: getChannelColorAvatar(selectedChannel) }}>
                <PersonIcon />
              </Avatar>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -8,
                  right: -8,
                  bgcolor: getChannelColor(selectedChannel),
                  borderRadius: '50%',
                  p: 0.1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 1,
                }}
              >
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', scale: '0.8'}}>
                  {getChannelIcon(selectedChannel)}
                </Box>
              </Box>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>{selectedUsername}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {toPersianDigits(messagesTotal.toString())} {t('conversations.messages')}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        ref={messagesContainerRef}
        data-mobile-scroll
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column-reverse',
          p: { xs: 1, sm: 2 },
          ...MESSAGE_BACKGROUND_SX,
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={4} sx={{ flexDirection: 'column' }}>
            <CircularProgress />
          </Box>
        ) : messages.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4} sx={{ flexDirection: 'column' }}>
            {t('conversations.noMessages')}
          </Typography>
        ) : (
          <>
            <div ref={messagesEndRef} />
            {messagesSkip > 0 && (
              <Box sx={MESSAGE_PAGINATION_BUTTON_WRAPPER_SX}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={loadPreviousMessages}
                  disabled={loadingMoreMessages}
                  sx={MESSAGE_PAGINATION_BUTTON_SX}
                >
                  {loadingMoreMessages ? (
                    <CircularProgress size={16} />
                  ) : (
                    t('conversations.loadMoreMessages')
                  )}
                </Button>
              </Box>
            )}
            {getOrderedMessages(messages)
              .reverse()
              .map((message) => (
              <Box key={message.id} sx={{ mb: 3, flexShrink: 0 }}>
                {/* User message (left side) */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1, alignItems: 'flex-start', gap: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: getChannelColor(selectedChannel) }}>
                    <PersonIcon />
                  </Avatar>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1, sm: 2 },
                      maxWidth: { xs: '90%', sm: '70%' },
                      width: 'fit-content',
                      minWidth: 0,
                      bgcolor: 'white',
                      borderRadius: '18px',
                      border: '1px solid #e0e0e0',
                      overflowWrap: 'break-word',
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                    }}
                  >
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontSize: { xs: '0.95rem', sm: '1rem' }, color: '#1C1C1C' }}>
                      {message.input_message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                      {formatLastMessageDatetime(message.created_at)}
                    </Typography>
                  </Paper>
                </Box>

                {/* AI response (right side) */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', gap: 1 }}>
                  <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1, sm: 2 },
                    maxWidth: { xs: '90%', sm: '50%' },
                    width: 'fit-content',
                    minWidth: 0,
                    bgcolor: '#D6E4FF',
                    color: '#1C1C1C',
                    borderRadius: '18px 18px 18px 18px',
                    overflowWrap: 'break-word',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ whiteSpace: 'pre-wrap', fontSize: { xs: '0.95rem', sm: '1rem' }, color: '#1C1C1C' }}
                      onClick={handleMarkdownButtonClick}
                      dangerouslySetInnerHTML={{ __html: parseMarkdown(message.output_message) }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: 'rgba(0, 0, 0, 0.6)' }}>
                        {formatLastMessageDatetime(message.created_at)}
                      </Typography>
                    </Box>
                  </Paper>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'white' }}>
                    <img src="./images/main.png" alt="" width={22}/>
                  </Avatar>
                </Box>
              </Box>
            ))}
            {hasMoreMessages && (
              <Box sx={MESSAGE_PAGINATION_BUTTON_WRAPPER_SX}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={loadMoreMessages}
                  disabled={loadingMoreMessages}
                  sx={MESSAGE_PAGINATION_BUTTON_SX}
                >
                  {loadingMoreMessages ? (
                    <CircularProgress size={16} />
                  ) : (
                    t('conversations.loadPreviousMessages')
                  )}
                </Button>
              </Box>
            )}
            <div ref={messagesTopRef} />
          </>
        )}
      </Box>
    </Card>
  );

  const renderMobileChannelDock = () => (
    <Box
      className="mobile-channel-dock"
      sx={{
        position: 'absolute',
        bottom: 10,
        left: 0,
        right: 0,
        zIndex: 10,
        display: 'flex',
        justifyContent: 'center',
        px: 1,
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          width: channelDockScrollable ? { xs: 'min(92vw, 320px)', sm: 360 } : 'max-content',
          maxWidth: { xs: 'min(92vw, 320px)', sm: 360 },
          flexShrink: 0,
          borderRadius: '22px',
          overflow: 'hidden',
          pointerEvents: 'auto',
          background: 'rgba(255, 255, 255, 0.65)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.55)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
        }}
      >
        <Box
          ref={channelDockScrollRef}
          className="mobile-channel-dock-scroll"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: channelDockScrollable ? 'flex-start' : 'center',
            gap: 1,
            px: 1,
            py: 1,
            width: channelDockScrollable ? '100%' : 'max-content',
            maxWidth: '100%',
            minWidth: 0,
            overflowX: channelDockScrollable ? 'auto' : 'hidden',
            overflowY: 'hidden',
            flexWrap: 'nowrap',
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorX: 'contain',
            touchAction: channelDockScrollable ? 'pan-x' : 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
        {channelsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.25, px: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          channels.map((channel) => {
            const isSelected = selectedChannel === channel;
            return (
              <Box
                key={channel}
                component="button"
                type="button"
                onClick={() => handleChannelSelect(channel)}
                aria-label={getChannelLabel(channel)}
                aria-pressed={isSelected}
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  border: 'none',
                  cursor: 'pointer',
                  bgcolor: 'transparent',
                  p: 0.25,
                  borderRadius: '14px',
                  minWidth: 44,
                  transition: 'transform 0.2s ease, background-color 0.2s ease',
                  transform: isSelected ? 'scale(1.04)' : 'scale(1)',
                  backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.85)' : 'transparent',
                  boxShadow: isSelected ? '0 2px 10px rgba(0, 0, 0, 0.08)' : 'none',
                  '&:active': { transform: 'scale(0.95)' },
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: getChannelColor(channel),
                    border: isSelected ? `2px solid ${getChannelColor(channel)}` : '2px solid transparent',
                    boxShadow: isSelected ? `0 0 0 1.5px rgba(255,255,255,0.9)` : 'none',
                    '& img, & .MuiSvgIcon-root': {
                      width: 20,
                      height: 20,
                      fontSize: 20,
                    },
                  }}
                >
                  {getChannelIcon(channel)}
                </Avatar>
              </Box>
            );
          })
        )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ py: 0, width: '100%', maxWidth: '100vw', padding: { xs: '8px', sm: '20px' } }}>
      <div style={{ height: '85vh', width: '100%', borderRadius: '12px' , border: '1px solid #e0e0e0' , display: 'flex' , overflowX: 'hidden'}} className='Desktop-conversation'>
        <div style={{ width: '140px', height: '100%', overflowY: 'auto' , display: 'flex' , flexDirection: 'column' , alignItems: 'center' , justifyContent: 'center' , borderLeft: '1px solid #e0e0e0'}} className="channels-container">
          <div style={{ width: '100%', height: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center' , fontSize: '1rem' , fontWeight: 'bold' , borderBottom: '1px solid #e0e0e0' , backgroundColor: '#f5f6fa'}}>
            {t('conversations.channels')}
          </div>
          <div style={{ width: '100%', height: '100%', overflowY: 'auto' , paddingTop: '10px'}}>
            {renderChannelList()}
          </div>
        </div>
        <div style={{ width: '360px', height: '100%', display: 'flex' , flexDirection: 'column' , borderLeft: '1px solid #e0e0e0'}} className="users-container">
          <div style={{ width: '100%', height: '63.5px', display: 'flex', alignItems: 'center', justifyContent: 'center' , fontSize: '1rem' , fontWeight: 'bold' , borderBottom: '1px solid #e0e0e0' , backgroundColor: '#f5f6fa', flexShrink: 0}}>
            {t('conversations.users')}
          </div>
          <div style={{ width: '100%', flex: 1, overflow: 'hidden' , padding: 10}}>
            {renderUserList()}
          </div>
        </div>
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}} className="messages-container">
          <div style={{ width: '100%', height: '63.5px', display: 'flex', alignItems: 'center', justifyContent: 'center' , fontSize: '1rem' , fontWeight: 'bold' , borderBottom: '1px solid #e0e0e0' , backgroundColor: '#f5f6fa', flexShrink: 0}}>
            {t('conversations.messages')}
          </div>
          <div style={{ width: '100%', flex: 1, overflow: 'hidden'}}>
            {currentLayer === 'messages' ? renderMessageList() : renderMessagesGuide()}
          </div>
        </div>
      </div>

      <Box
        className="Mobile-conversation"
        sx={{
          height: '85vh',
          width: '100%',
          borderRadius: '12px',
          border: '1px solid #e0e0e0',
          display: 'none',
          overflow: 'hidden',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            overflow: 'hidden',
            width: '100%',
          }}
        >
          {currentLayer === 'messages' ? (
            <Box className="messages-container" sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {renderMessageList()}
            </Box>
          ) : currentLayer === 'users' && selectedChannel ? (
            <Box className="users-container" sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', borderLeft: '1px solid #e0e0e0', overflow: 'hidden' }}>
              <Box
                sx={{
                  width: '100%',
                  height: { xs: 48, sm: 56 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 'bold',
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: '#f5f6fa',
                  flexShrink: 0,
                }}
              >
                {t('conversations.users')}
              </Box>
              <Box sx={{ width: '100%', flex: 1, overflow: 'hidden', p: { xs: 0.5, sm: 1 } }}>
                {renderUserList()}
              </Box>
            </Box>
          ) : (
            <Box className="messages-container" sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', borderLeft: '1px solid #e0e0e0', overflow: 'hidden' }}>
              <Box
                sx={{
                  width: '100%',
                  height: { xs: 48, sm: 56 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 'bold',
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: '#f5f6fa',
                  flexShrink: 0,
                }}
              >
                {t('conversations.messages')}
              </Box>
              <Box sx={{ width: '100%', flex: 1, overflow: 'hidden' }}>
                {renderMessagesGuide()}
              </Box>
            </Box>
          )}
        </Box>
        {renderMobileChannelDock()}
      </Box>
    </Box>
  );
};

const ConversationsPage: FC = () => {
  return (
    <TourProvider steps={useTranslatedSteps().steps_conversation}>
      <VideoProvider videoUrl={videoUrls.chatHistory} videoTitle={videoTitles.chatHistory}>
        <ConversationsPageContent />
      </VideoProvider>
    </TourProvider>
  );
};

export default ConversationsPage;